import React, { useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import Input from 'components/Common/Input';
import Radio from 'components/Common/Radio';
import Button from 'components/Common/Button';
import { MdDelete, MdMic } from 'react-icons/md';
import TelInput from 'components/Common/TelInput';
import { IoChevronDownOutline } from 'react-icons/io5';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTalks } from 'apis/business/agent/talks/hook';
import { useAgent } from 'providers/context/AgentProvider';
import { a11yProps } from 'components/Common/CustomTabPanel';
import { TalksRequest } from 'apis/business/agent/talks/types';
import {
  Drawer,
  Box,
  Typography,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Accordion,
  Tabs,
  Tab
} from '@mui/material';
import { IoClose } from 'react-icons/io5';
import LiveKitAudioTest from './LiveKitAudioTest';

interface TalkButtonProps {
  isCollapsed?: boolean;
  label?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  sx?: any;
}

export default function TalkButton({ isCollapsed = false, onClick, label = 'Talk', icon, sx = {} }: TalkButtonProps) {
  const [isTalkModalOpen, setIsTalkModalOpen] = React.useState(false);
  const [value, setValue] = useState<number>(0);
  const { selectedAgent } = useAgent();
  const { mutateAsync: createTalks, isPending } = useTalks();
  const { control, handleSubmit, reset, watch } = useForm<TalksRequest>({
    defaultValues: {
      number_type: 'phone'
    }
  });
  const numberType = watch('number_type');
  const {
    fields: customFields,
    append: fieldAppend,
    remove: fieldRemove
  } = useFieldArray({
    control,
    name: 'custom_variables'
  });

  const handleCloseTalkModal = () => {
    reset();
    setIsTalkModalOpen(false);
  };

  const handleClickButton = () => {
    onClick && onClick();
    setIsTalkModalOpen(true);
  };

  const onSubmit = async (data: TalksRequest) => {
    delete data.number_type;
    await createTalks(data);
    handleCloseTalkModal();
  };

  return (
    <>
      <Button
        fullWidth={!isCollapsed}
        size='small'
        onClick={() => handleClickButton()}
        sx={{
          ...sx,
          justifyContent: 'center',
          transition: 'padding 0.3s, justify-content 0.3s',
          minWidth: 'unset'
        }}
      >
        {label === 'Talk' && !icon && <MdMic style={{ fontSize: 18, marginRight: !isCollapsed ? 8 : 0 }} />}
        {icon && (
          <Box component='span' sx={{ display: 'inline-flex', mr: !isCollapsed ? 1 : 0 }}>
            {icon}
          </Box>
        )}
        {!isCollapsed && <Typography>{label}</Typography>}
      </Button>
      <Drawer
        anchor='right'
        open={isTalkModalOpen}
        onClose={() => handleCloseTalkModal()}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 500 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 100001
          }
        }}
      >
        {/* Fixed Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #DEDEDE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Typography variant='h6'>Talk To {selectedAgent?.name}</Typography>
          <IconButton onClick={() => handleCloseTalkModal()} size='small'>
            <IoClose size={24} />
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            p: 3
          }}
        >
          <Box bgcolor='background.paper' mt={0} mb={2} py={0.5} sx={{ flexShrink: 0 }}>
            <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} aria-label='configure tabs'>
              <Tab sx={{ width: '50%' }} label='Phone' {...a11yProps('talk', 'talkpanel', 0)} />
              <Tab sx={{ width: '50%' }} label='Web' {...a11yProps('talk', 'talkpanel', 1)} />
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {value === 0 && (
              <Box component='form' onSubmit={handleSubmit(onSubmit)}>
                <Box>
                  <Accordion
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid #F3F3F3',
                      marginTop: 2,
                      '::before': { background: 'transparent' }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                      aria-controls='panel1-content'
                      id='panel1-header'
                    >
                      <Box display='flex' flexDirection='column'>
                        <Typography variant='h6' fontSize={16} fontWeight={600}>
                          Custom Variables
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {customFields.map((field: any, index: number) => (
                        <Box
                          key={field.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            mb: 3
                          }}
                        >
                          <Input
                            name={`custom_variables[${index}].key`}
                            size='small'
                            label='Key'
                            control={control}
                            sx={{ flex: 2 }}
                            fullWidth
                          />
                          <Input
                            name={`custom_variables[${index}].value`}
                            size='small'
                            label='Value'
                            control={control}
                            sx={{ flex: 2 }}
                            fullWidth
                          />
                          <IconButton size='small' color='error' onClick={() => fieldRemove(index)}>
                            <MdDelete />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        type='button'
                        color='secondary'
                        size='small'
                        variant='outlined'
                        startIcon={<FaPlus />}
                        onClick={() => fieldAppend({ key: '', value: '' })}
                        sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                      >
                        Add Variable
                      </Button>
                    </AccordionDetails>
                  </Accordion>
                  <Radio
                    name='number_type'
                    control={control}
                    label=''
                    options={[
                      { label: 'Phone', value: 'phone' },
                      { label: 'Extension', value: 'extension' }
                    ]}
                  />
                  {numberType === 'phone' ? (
                    <TelInput
                      name='to_number'
                      size='small'
                      control={control}
                      placeholder='e.g. +1234567890'
                      defaultCountry='US'
                      fullWidth
                      required
                    />
                  ) : (
                    <>
                      <Input
                        name='to_number'
                        size='small'
                        control={control}
                        placeholder='e.g. 401'
                        fullWidth
                        required
                      />
                      <Typography
                        color='#1E66F5'
                        marginTop={2}
                        fontSize={14}
                        bgcolor='#F0F6FF'
                        border='1px solid #D0E3FF'
                        borderRadius={2}
                        padding={1}
                      >
                        Dialing extensions will work if your telephony system is integrated with SIP and has extensions
                        set up for the agent.
                      </Typography>
                    </>
                  )}
                </Box>
                <Button type='submit' isPending={isPending} sx={{ mt: 2 }} fullWidth>
                  Call Me
                </Button>
              </Box>
            )}

            {value === 1 && (
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <LiveKitAudioTest isInDrawer={true} />
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
